if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');
        this.selectedUpsellVariants = new Set();
        this.parentProductIdInput = this.form.querySelector('[name="id"], [name="items[0][id]"]');
        this.upsellButtons = [...(this.closest('product-info')?.querySelectorAll('.product-upsell__button[data-upsell-variant-id]') || [])];
        this.bindUpsellButtons();

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      bindUpsellButtons() {
        if (!this.upsellButtons.length) return;

        this.upsellButtons.forEach((button) => {
          this.setUpsellButtonState(button, 'add');

          button.addEventListener('click', async () => {
            const variantId = button.dataset.upsellVariantId;
            if (!variantId || button.classList.contains('is-loading')) return;

            const isSelected = button.classList.contains('is-selected');

            if (isSelected) {
              this.selectedUpsellVariants.delete(variantId);
              this.setUpsellButtonState(button, 'add');
              return;
            }

            this.setUpsellButtonState(button, 'loading');
            await this.delay(500);

            this.selectedUpsellVariants.add(variantId);
            this.setUpsellButtonState(button, 'remove');
          });
        });
      }

      setUpsellButtonState(button, state) {
        const spinnerEl = button.querySelector('.product-upsell__button-spinner .loading__spinner');

        button.dataset.state = state;
        button.classList.toggle('is-loading', state === 'loading');
        button.classList.toggle('is-selected', state === 'remove');
        if (spinnerEl) spinnerEl.classList.toggle('hidden', state !== 'loading');
      }

      getPrimaryQuantityInput() {
        const formId = this.form?.id;
        if (!formId) return this.form?.querySelector('[name="quantity"], [name="items[0][quantity]"]') || null;

        return (
          document.querySelector(`input[name="quantity"][form="${formId}"]`) ||
          document.querySelector(`input[name="items[0][quantity]"][form="${formId}"]`) ||
          this.form?.querySelector('[name="quantity"], [name="items[0][quantity]"]') ||
          null
        );
      }

      delay(ms) {
        return new Promise((resolve) => setTimeout(resolve, ms));
      }

      getSelectedUpsellVariantIds() {
        if (this.selectedUpsellVariants.size) return [...this.selectedUpsellVariants];

        return [...document.querySelectorAll('.product-upsell__button[data-state="remove"][data-upsell-variant-id]')]
          .map((button) => button.dataset.upsellVariantId)
          .filter(Boolean);
      }

      normalizeUpsellPayload(formData) {
        const parentId = this.parentProductIdInput?.value || formData.get('id') || formData.get('items[0][id]');
        const parentQuantity = this.getPrimaryQuantityInput()?.value || formData.get('quantity') || '1';
        const syncedQuantity = parentQuantity || '1';
        const upsells = this.getSelectedUpsellVariantIds();

        formData.delete('id');
        formData.delete('quantity');
        formData.delete('items[0][id]');
        formData.delete('items[0][quantity]');

        if (!upsells.length) {
          if (parentId) formData.append('id', parentId);
          formData.append('quantity', syncedQuantity);
          return;
        }

        if (parentId) formData.append('items[0][id]', parentId);
        formData.append('items[0][quantity]', syncedQuantity);
        upsells.forEach((variantId, index) => {
          const upsellIndex = index + 1;
          formData.append(`items[${upsellIndex}][id]`, variantId);
          formData.append(`items[${upsellIndex}][quantity]`, syncedQuantity);
          formData.append(`items[${upsellIndex}][properties][_is_upsell]`, 'true');
        });
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        this.normalizeUpsellPayload(formData);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id') || formData.get('items[0][id]'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location.href = window.routes.checkout_url;
              return;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id') || formData.get('items[0][id]'),
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
